import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  LTButton,
  LTCopyToClipboard,
  LTSegmentedControl,
  LTTag,
  LTText,
  LTTimeline,
  LTTooltip
} from '@lambdatestincprivate/lt-components';
import RCAIcons from './RCAIcons';
import StackTraceViewer from './StackTraceViewer';
import RCAMediaTab from './RCAMediaTab';
import { getRootCauseIndex, getStepMedia } from './helpers';
import { TagStyles, DETAIL_TAB } from './constants';
import { RCA_EVENTS } from './events';
import SubTimelineConnector from '../../assets/SubTimelineConnector';
import styles from './AIRca.module.css';

/**
 * Expanded detail panel for a timeline step. Renders the stack trace and the
 * media (expected/actual screenshots) as tabs — the tab bar only appears when
 * both are available; with a single source the content shows directly.
 */
const RCAStepDetails = ({ step, media: mediaProp, mediaSteps, mediaIndex }) => {
  // media is computed once by the caller (for its hasDetails check) and passed
  // down to avoid recomputing getStepMedia per step; fall back if omitted.
  const media = mediaProp || getStepMedia(step);
  const hasStack = Boolean(step.stack_trace);
  const hasMedia = media.length > 0;

  const [activeTab, setActiveTab] = useState(
    hasStack ? DETAIL_TAB.LOGS : DETAIL_TAB.MEDIA
  );

  if (!hasStack && !hasMedia) return null;

  const showTabs = hasStack && hasMedia;
  // The first tab is named after the log source (ScenarioLogs / CommandLogs /
  // etc.) so it doubles as the section header — no separate header row needed.
  const logLabel = step.source_log || 'Stacktrace';

  const tabOptions = [
    { label: logLabel, value: DETAIL_TAB.LOGS },
    { label: 'Media', value: DETAIL_TAB.MEDIA }
  ];

  return (
    <div className={styles.stackTraceContainer}>
      {showTabs && (
        <div className={styles.detailsTabs}>
          <LTSegmentedControl
            ariaLabel="Step detail tabs"
            options={tabOptions}
            selectedValue={activeTab}
            onChange={(index, option) => setActiveTab(option.value)}
          />
          {activeTab === DETAIL_TAB.LOGS && (
            <div className={styles.copyActions}>
              <LTCopyToClipboard textToCopy={step.stack_trace} />
            </div>
          )}
        </div>
      )}

      {hasStack && activeTab === DETAIL_TAB.LOGS && (
        <>
          {!showTabs && (
            <div className={styles.stackTraceHeader}>
              <LTText variant={LTText.TextVariant.SUBHEADER_BOLD}>
                {step.source_log}
              </LTText>
              <div className={styles.copyActions}>
                <LTCopyToClipboard textToCopy={step.stack_trace} />
              </div>
            </div>
          )}
          <div className={styles.stackTraceBody}>
            <StackTraceViewer value={step.stack_trace} />
          </div>
        </>
      )}

      {hasMedia && activeTab === DETAIL_TAB.MEDIA && (
        <div className={styles.mediaTabBody}>
          <RCAMediaTab
            media={media}
            stepName={step.step_name}
            stepSummary={step.summary}
            mediaSteps={mediaSteps}
            mediaIndex={mediaIndex}
          />
        </div>
      )}
    </div>
  );
};

const RCATimeline = ({ timelineData, onTrack }) => {
  // Compute timeline data before state initialization
  const rootCauseIndex = getRootCauseIndex(
    Array.isArray(timelineData) ? timelineData : []
  );
  const errorsBeforeRootCause = Array.isArray(timelineData)
    ? timelineData.slice(0, rootCauseIndex + 1)
    : [];
  const errorsAfterRootCause = Array.isArray(timelineData)
    ? timelineData.slice(rootCauseIndex + 1)
    : [];

  // Flat list (in timeline order) of every step that has media, used to let the
  // enlarged comparison navigate across steps. mediaIndexByStep maps a step to
  // its position in that list so each step's media can open at the right slot.
  const { mediaSteps, mediaIndexByStep } = useMemo(() => {
    const list = [];
    const map = new Map();
    const steps = Array.isArray(timelineData) ? timelineData : [];
    steps.forEach((step, index) => {
      const stepMedia = getStepMedia(step);
      if (stepMedia.length > 0) {
        // Same tagging rule as the timeline: the root cause is tagged
        // "Root Cause", steps after it are "Effect", earlier steps untagged.
        let tag = null;
        if (rootCauseIndex >= 0 && index === rootCauseIndex) tag = 'Root Cause';
        else if (rootCauseIndex >= 0 && index > rootCauseIndex) tag = 'Effect';
        map.set(step, list.length);
        list.push({
          tag,
          stepName: step.step_name,
          stepSummary: step.summary,
          media: stepMedia
        });
      }
    });
    return { mediaSteps: list, mediaIndexByStep: map };
  }, [timelineData, rootCauseIndex]);

  // Auto-expand Root Cause and Last Effect by default (Req-2: TE-7518)
  // Initialize state directly from timelineData so they are expanded on
  // the very first render — no flash of collapsed content.
  const [selectedStep, setSelectedStep] = useState(
    rootCauseIndex >= 0 ? rootCauseIndex : null
  );
  const [selectedSubStep, setSelectedSubStep] = useState(
    rootCauseIndex >= 0 && errorsAfterRootCause.length > 0
      ? errorsAfterRootCause.length - 1
      : null
  );

  // Re-expand when timeline data changes (e.g. different test selected
  // while the drawer stays mounted).
  const prevTimelineRef = useRef(timelineData);
  useEffect(() => {
    if (prevTimelineRef.current === timelineData) return;
    prevTimelineRef.current = timelineData;
    if (rootCauseIndex >= 0 && Array.isArray(timelineData) && timelineData.length > 0) {
      setSelectedStep(rootCauseIndex);
      setSelectedSubStep(
        errorsAfterRootCause.length > 0 ? errorsAfterRootCause.length - 1 : null
      );
    } else {
      setSelectedStep(null);
      setSelectedSubStep(null);
    }
  }, [timelineData, rootCauseIndex, errorsAfterRootCause.length]);

  // Log only expansions (not collapses) — a collapse isn't a content view.
  const trackExpand = (stepName, stepIndex, isRootCause, willExpand) => {
    if (willExpand) {
      onTrack?.(RCA_EVENTS.TIMELINE_STEP_EXPANDED, {
        step_name: stepName,
        step_index: stepIndex,
        is_root_cause: isRootCause,
      });
    }
  };

  if (!Array.isArray(timelineData) || timelineData.length === 0) return null;

  return (
    <div className={styles.timelineContainer}>
      <div className={styles.timelineHeader}>
        <LTText variant={LTText.TextVariant.SUBHEADER_BOLD} color="text.muted">
          Event Timeline
        </LTText>
      </div>
      <div className={styles.timelineWrapper}>
        <LTTimeline>
          {errorsBeforeRootCause.map((step, index) => {
            const isExpanded = index === selectedStep;
            // Only the root cause is tagged here; the steps leading up to it
            // (before the root cause) carry no tag. "Effect" applies solely to
            // the sub-steps rendered after the root cause.
            const timelineTagText =
              rootCauseIndex === index ? 'Root Cause' : null;
            // This list is slice(0, rootCauseIndex + 1), so index <= rootCauseIndex
            // always — no after-root-cause ("Effect") step reaches this branch.
            const iconName = step?.level;
            const media = getStepMedia(step);
            const hasDetails = Boolean(step.stack_trace) || media.length > 0;

            return (
              <LTTimeline.Item
                key={index}
                badgeIcon={<RCAIcons entity={iconName} />}
              >
                <LTTimeline.Body>
                  <div className={styles.stepContent}>
                    <div className={styles.stepHeader}>
                      <div className={styles.stepHeaderMain}>
                        <LTTooltip text={step.step_name}>
                          <LTText
                            variant={LTText.TextVariant.SUBHEADER_BOLD}
                            className={styles.stepTitle}
                          >
                            {step.step_name}
                          </LTText>
                        </LTTooltip>
                        <div>
                          {timelineTagText && (
                            <LTTag
                              customStyle={TagStyles[timelineTagText]}
                              text={timelineTagText}
                            />
                          )}
                        </div>
                      </div>
                      {hasDetails && (
                        <LTButton
                          variant={LTButton.Variant.INVISIBLE}
                          size={LTButton.Size.SMALL}
                          className={styles.stepToggle}
                          onClick={() => {
                            const willExpand = !isExpanded;
                            setSelectedStep(willExpand ? index : null);
                            trackExpand(
                              step.step_name,
                              index,
                              rootCauseIndex === index,
                              willExpand
                            );
                          }}
                        >
                          <LTText
                            variant={LTText.TextVariant.SUBHEADER_REGULAR}
                            className={styles.stepToggleText}
                          >
                            {isExpanded ? 'Hide' : 'Show'}
                          </LTText>
                        </LTButton>
                      )}
                    </div>
                    <LTText
                      variant={LTText.TextVariant.SMALL_REGULAR}
                      color="text.muted"
                    >
                      {step.summary}
                    </LTText>
                    {isExpanded && hasDetails && (
                      <RCAStepDetails
                        step={step}
                        media={media}
                        mediaSteps={mediaSteps}
                        mediaIndex={mediaIndexByStep.get(step)}
                      />
                    )}
                  </div>

                  {index === rootCauseIndex && errorsAfterRootCause.length > 0 && (
                    <div>
                      <div>
                        {errorsAfterRootCause.map((subStep, subIndex) => {
                          const isSubExpanded = subIndex === selectedSubStep;
                          const subIconName =
                            subIndex > rootCauseIndex ? 'W' : subStep?.level;
                          const subMedia = getStepMedia(subStep);
                          const subHasDetails =
                            Boolean(subStep.stack_trace) || subMedia.length > 0;

                          return (
                            <div
                              key={subIndex}
                              className={styles.subTimelineRow}
                            >
                              <div className={styles.subTimelineConnector}>
                                <SubTimelineConnector />
                              </div>
                              <div className={styles.subStepContent}>
                                <div className={styles.stepHeader}>
                                  <div className={styles.stepHeaderMain}>
                                    <RCAIcons entity={subIconName} />
                                    <div className={styles.subStepInner}>
                                      <div className={styles.subStepNameRow}>
                                        <div className={styles.subStepNameTags}>
                                          <LTTooltip text={subStep.step_name}>
                                            <LTText
                                              variant={
                                                LTText.TextVariant
                                                  .SUBHEADER_BOLD
                                              }
                                              className={styles.stepTitle}
                                            >
                                              {subStep.step_name}
                                            </LTText>
                                          </LTTooltip>
                                          <LTTag
                                            customStyle={TagStyles['Effect']}
                                            text="Effect"
                                          />
                                        </div>
                                        {subHasDetails && (
                                          <LTButton
                                            variant={LTButton.Variant.INVISIBLE}
                                            size={LTButton.Size.SMALL}
                                            className={styles.stepToggle}
                                            onClick={() => {
                                              const willExpand = !isSubExpanded;
                                              setSelectedSubStep(
                                                willExpand ? subIndex : null
                                              );
                                              trackExpand(
                                                subStep.step_name,
                                                subIndex,
                                                false,
                                                willExpand
                                              );
                                            }}
                                          >
                                            <LTText
                                              variant={
                                                LTText.TextVariant
                                                  .SUBHEADER_REGULAR
                                              }
                                              className={styles.stepToggleText}
                                            >
                                              {isSubExpanded ? 'Hide' : 'Show'}
                                            </LTText>
                                          </LTButton>
                                        )}
                                      </div>
                                      <LTText
                                        variant={
                                          LTText.TextVariant.SMALL_REGULAR
                                        }
                                        color="text.muted"
                                      >
                                        {subStep.summary}
                                      </LTText>
                                    </div>
                                  </div>
                                </div>
                                {isSubExpanded && subHasDetails && (
                                  <RCAStepDetails
                                    step={subStep}
                                    media={subMedia}
                                    mediaSteps={mediaSteps}
                                    mediaIndex={mediaIndexByStep.get(subStep)}
                                  />
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </LTTimeline.Body>
              </LTTimeline.Item>
            );
          })}

          {errorsBeforeRootCause.length > 0 && (
            <LTTimeline.Item
              key="session-finished"
              badgeIcon={<span className={styles.sessionDot} />}
            >
              <div className={styles.sessionContent}>
                <div className={styles.sessionHeader}>
                  <div className={styles.sessionHeaderMain}>
                    <LTTooltip text="Session Finished">
                      <LTText
                        text="Session Finished"
                        style={{ marginTop: '6px' }}
                        variant={LTText.TextVariant.SUBHEADER_REGULAR}
                      />
                    </LTTooltip>
                  </div>
                </div>
              </div>
            </LTTimeline.Item>
          )}
        </LTTimeline>
      </div>
    </div>
  );
};

export default RCATimeline;
